import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ChevronRight, Clock, ArrowRight } from 'lucide-react';
import { Seo } from '../components/Seo';
import { SITE, blogPostJsonLd, breadcrumbJsonLd } from '../data/seoData';
import { BLOG_POSTS } from '../data/blogData';

export default function BlogPost() {
  const { blogSlug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === blogSlug);
  if (!post) return <Navigate to="/blog" replace />;

  const jsonLd = [
    blogPostJsonLd(post),
    breadcrumbJsonLd([
      { name: 'Home', path: '/' },
      { name: 'Blog', path: '/blog' },
      { name: post.title, path: `/blog/${post.slug}` },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white" data-testid={`blog-post-${post.slug}`}>
      <Seo title={post.metaTitle} description={post.metaDescription} path={`/blog/${post.slug}`} jsonLd={jsonLd} />

      <article className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        <nav className="flex items-center gap-1.5 text-sm text-[#1F3D2C]/60 mb-6" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#4A7C59]">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to="/blog" className="hover:text-[#4A7C59]">Blog</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-[#14281D] font-medium truncate">{post.tag}</span>
        </nav>

        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.1] text-[#14281D]">{post.title}</h1>
        <div className="mt-4 flex items-center gap-4 text-sm text-[#4A7C59]">
          <span>{new Date(post.date + 'T12:00:00').toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {post.readTime}</span>
        </div>

        <p className="mt-8 text-base md:text-lg text-[#1F3D2C]/85 leading-relaxed">{post.intro}</p>

        {post.sections.map((s) => (
          <section key={s.heading} className="mt-10">
            <h2 className="text-base md:text-lg font-bold text-[#14281D] mb-3">{s.heading}</h2>
            {s.paragraphs.map((p, i) => (
              <p key={i} className="text-[#1F3D2C]/80 leading-relaxed text-sm md:text-base mb-3">{p}</p>
            ))}
            {s.list && (
              <ol className="list-decimal pl-5 space-y-2 text-[#1F3D2C]/80 text-sm md:text-base">
                {s.list.map((item) => <li key={item}>{item}</li>)}
              </ol>
            )}
            {s.table && (
              <div className="overflow-x-auto rounded-xl border border-[#E5E7EB] mt-2" data-testid="blog-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#14281D] text-white">
                      {s.table.headers.map((h) => <th key={h} className="px-4 py-3 text-left font-semibold">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {s.table.rows.map((row, ri) => (
                      <tr key={ri} className={ri % 2 ? 'bg-[#F4F7F2]' : 'bg-white'}>
                        {row.map((cell, ci) => (
                          <td key={ci} className={`px-4 py-3 text-[#1F3D2C] ${ci === 0 ? 'font-semibold text-[#14281D]' : ''}`}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ))}

        {/* CTA */}
        <div className="mt-12 bg-[#F4F7F2] rounded-2xl p-8 text-center">
          <h2 className="text-base md:text-lg font-bold text-[#14281D] mb-2">Ready to print yours?</h2>
          <p className="text-sm md:text-base text-[#1F3D2C]/70 mb-5">Any size, shape and quantity — priced instantly online.</p>
          <Link to="/quote" data-testid={`blog-${post.slug}-quote-cta`}>
            <button className="bg-[#6FBF73] hover:bg-[#4A7C59] text-white px-7 py-3 rounded-full text-base font-semibold shadow-md transition-colors inline-flex items-center gap-2">
              Get an Instant Quote <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>

        {/* Related */}
        <div className="mt-10">
          <h2 className="text-base md:text-lg font-bold text-[#14281D] mb-4">Related products</h2>
          <div className="flex flex-wrap gap-3">
            {post.related.map((r) => (
              <Link
                key={r.to}
                to={r.to}
                className="px-4 py-2 rounded-full border border-[#6FBF73] text-[#2F5D3A] hover:bg-[#6FBF73] hover:text-white text-sm font-semibold transition-colors"
              >
                {r.label}
              </Link>
            ))}
          </div>
        </div>
      </article>

      <footer className="bg-[#14281D] text-white py-10">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-emerald-100">© 2026 {SITE.name}. All rights reserved.</p>
          <p className="text-sm text-emerald-200/80 mt-2">
            {SITE.address.street}, {SITE.address.city}, {SITE.address.region} {SITE.address.postal}
          </p>
          <p className="text-sm text-emerald-200/80 mt-1">Phone: {SITE.phone} | Email: {SITE.email}</p>
        </div>
      </footer>
    </div>
  );
}
